import React, { useState, useEffect } from "react";
import CardDropdown from "../../Common/CardDropdown/CardDropdown";
import usdtIcon from "../../../styles/images/usdt_logo.png";
import uzsIcon from "../../../styles/images/uzb_flag.jpg";
import { useProfile } from '../../../hooks/useProfile';
import { handleApiError, exchangeAPI, cardAndWalletAPI, paymentAPI } from "../../../services/api";
import { useTelegram } from "../../../hooks/useTelegram";
import WalletDropdown from "../../Common/WalletDropdown/WalletDropdown";
import TronHashInput from "../../Common/TronHashInput/TronHashInput";

import { useTranslation } from "react-i18next";

const ExchangeForm = ({ activeTab }) => {
  const { t } = useTranslation("exchange");
  const { t: err } = useTranslation("exchange_error");

  const [fromAmount, setFromAmount] = useState("");
  const [toAmount, setToAmount] = useState("");

  const [tronHash, setTronHash] = useState("");

  const [selectedCard, setSelectedCard] = useState(null);
  const [cards, setCards] = useState([]);

  const [selectedWallet, setSelectedWallet] = useState(null);
  const [wallets, setWallets] = useState([]);

  const [requestLoading, setRequestLoading] = useState(false);

  const { profile, error, loading, fetchProfile } = useProfile();
  const [exchangeRate, setExchangeRate] = useState(0);

  const [showOtpForm, setShowOtpForm] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  const [showMessage, setShowMessage] = useState("");

  const [isError, setIsError] = useState(false);

  const { showAlert } = useTelegram();

  const getFromCurrency = () => (activeTab === "buy" ? "USDT" : "USDT");
  const getToCurrency = () => (activeTab === "buy" ? "UZS" : "UZS");

  const setErr = (msg) => {
    setIsError(true)
    setShowMessage(msg)
  }

  const setMsg = (msg) => {
    setShowMessage(msg)
  }

  const getCurrencyIcon = (currency) => {
    if (currency === "USDT") {
      return <img src={usdtIcon} alt="USDT" className="w-4 h-4 rounded-full" />;
    }
    if (currency === "UZS") {
      return <img src={uzsIcon} alt="UZS" className="w-4 h-4 rounded-full" />;
    }
    return <span className="w-4 h-4 rounded-full bg-gray-400 inline-block"></span>;
  };

  const getCurrencyColor = (currency) => {
    return currency === "USDT"
      ? "bg-green-100 text-green-800"
      : "bg-cyan-100 text-cyan-800";
  };

  const resetMessage = () => {
    setShowMessage("")
    setIsError(false)
  }

  const getMethodLabel = (currency, isFromField) => {
    if (activeTab === "buy") {
      if (currency === "UZS") return t("BUY_UZS_TITLE")
      if (currency === "USDT") return t("BUY_USDT_TITLE")
    } else {
      if (currency === "USDT" && isFromField) return t("SELL_UZS_TITLE")
      if (currency === "UZS" && !isFromField) return t("SELL_USDT_TITLE")
    }
    return ""; // fallback
  };

  const formatNumberWithCommas = (num) => {
    if (!num) return "";
    const cleanNum = num.toString().replace(/[^\d.]/g, "");
    const parts = cleanNum.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.length > 1 ? `${parts[0]}.${parts[1].slice(0, 2)}` : parts[0];
  };

  const parseFormattedNumber = (str) => {
    if (!str) return 0;
    return parseFloat(str.replace(/,/g, "")) || 0;
  };

  const handleInputChange = async (value, setter, isFromField = true) => {
    const cleanValue = value.replace(/[^\d.]/g, "");
    const dotCount = (cleanValue.match(/\./g) || []).length;
    if (dotCount > 1) return;

    const parts = cleanValue.split('.');
    if (parts[1] && parts[1].length > 2) {
      parts[1] = parts[1].slice(0, 2);
    }
    const validValue = parts.join('.');
    const formattedValue = formatNumberWithCommas(validValue);
    setter(formattedValue);

    const numValue = parseFormattedNumber(formattedValue);
    await handleExchangeRate(numValue)

  };

  const updateSelected = () => {
    setCards(profile.cards)
    setWallets(profile.wallets)

    setSelectedCard(null)
    setSelectedWallet(null)

    if (profile.cards !== 0) {
      setSelectedCard(profile.cards[0])
    }

    if (profile.wallets !== 0) {
      setSelectedWallet(profile.wallets[0])
    }
  }

  useEffect(() => {

    handleExchangeRate(1000, true);

    if (activeTab === "buy") {
      setFromAmount("");// USDT
      setToAmount(""); // UZS 
    } else {
      setFromAmount(""); // USDT
      setToAmount(""); // UZS
    }

    if (loading) return;
    if (error) return;

    updateSelected()
  }, [activeTab, profile]);

  const handleExchange = async () => {
    if (activeTab === "buy") {
      try {
        setRequestLoading(true)
        const response = await paymentAPI.MakeBuyUsdtInit(selectedCard.id, fromAmount, selectedWallet.id);
        if (response.data.sended === true)
          setShowOtpForm(true)
        else {
          setErr(err("BUY_ERR"))
        }
      } catch (e) {
        setErr(err("BUY_ERR") + ": " + e.response?.data?.error)
      } finally {
        setRequestLoading(false)
      }
    } else if (activeTab === "sell") {
      try {
        setRequestLoading(true)
        const response = await paymentAPI.MakeSellUsdtInit(selectedCard.id, tronHash, fromAmount);
        if (response.data.sended === true) {
          setShowOtpForm(true)
        } else {

          setErr(err("SELL_ERR"))
        }
      } catch (e) {
        setErr(err("SELL_ERR") + ": " + e?.response?.data?.error)
      } finally {
        setRequestLoading(false)
      }
    } else {
      setErr(err("OPERATION_INIT_ERR"))
    }
  }

  const handleOtp = async () => {
    if (!otpCode.trim()) {
      return;
    }
    try {
      setRequestLoading(true)
      if (activeTab === "buy") {
        const response = await paymentAPI.MakeBuyUsdtConfirm(otpCode);
        if (response.data.registered) {
          setShowMessage(t("BUY_SUCCESS"));
          setShowOtpForm(false);
          setFromAmount("");// USDT
          setToAmount(""); // UZS 
          setOtpCode("");
        } else {
          setErr(err("SMS_ERR"));
        }
      } else {
        const response = await paymentAPI.MakeSellUsdtConfirm(otpCode);
        if (response.data.registered) {
          setShowMessage(t("SELL_SUCCESS"));
          setShowOtpForm(false);
          setFromAmount("");// USDT
          setToAmount(""); // UZS 
          setOtpCode("");
          setTronHash("");
        } else {
          setErr(err("SMS_ERR"));
        }
      }
    } catch (e) {
      setErr(err("SMS_SEND_ERR") + ": " + e?.response?.data?.error)
    }
    finally {
      setRequestLoading(false)
      setShowOtpForm(false);
      setOtpCode("")
    }
  }

  const handleHashChange = (value) => {
    let cleaned = value.trim().toLowerCase();
    cleaned = cleaned.replace(/[^0-9a-f]/g, "");

    if (cleaned.length > 64) {
      cleaned = cleaned.slice(0, 64);
    }

    setTronHash(cleaned);
  };

  const handleCardAdd = async (card) => {
    try {
      const response = await cardAndWalletAPI.AddCardInit(card.pan, card.expiryDate, card.name);
      if (response.data.sended !== true) {
        setErr(err("CARD_ADD_ERR"))
        return false
      }
      else {
        return true
      }
    } catch (err) {
      setErr(err("CARD_ADD_ERR") + ": " + err?.response?.data?.error)
    } finally {
      return false
    }
  }

  const handleCardOtp = async (otp) => {
    try {
      const response = await cardAndWalletAPI.AddCardOTP(otp);

      if (response.data.registered === true) {
        setMsg(t("CARD_ADD_SUCCESS"))
        await fetchProfile();
      }
      else {
        setErr(err("SMS_SEND_ERR"))
      }
    } catch (err) {
      setErr(err("SMS_SEND_ERR") + ": " + error?.response?.data?.error)
    }
  }

  const handleWalletAdd = async (wallet) => {
    try {
      const response = await cardAndWalletAPI.AddWallet(wallet.address, wallet?.name);
      if (response.data.registered === true) {
        setMsg(t("WALLET_ADD_SUCCESS"))
        await fetchProfile();
      } else {
        setErr(err("WALLET_ADD_ERR"))
      }
    } catch (err) {
      setErr(err("WALLET_ADD_ERR") + ": " + error?.response?.data?.error)
    }
  }

  const handleWalletDelete = async (walletId) => {
    try {
      const response = await cardAndWalletAPI.RemoveWallet(walletId);
      if (response.status === 200) {
        if (selectedWallet) {
          if (selectedWallet.id === walletId) setSelectedWallet(null)
        }
        setMsg(t("WALLET_DELETE_SUCCESS"))
        await fetchProfile();
      } else {
        setErr(err("WALLET_DELETE_ERR"))
      }
    } catch (err) {
      setErr(err("WALLET_DELETE_ERR") + ": " + error?.response?.data?.error)
    }
  }

  const handleCardDelete = async (cardId) => {
    try {
      const response = await cardAndWalletAPI.RemoveCard(cardId);
      if (response.status === 200) {
        if (selectedCard) {
          if (selectedCard.id === cardId) setSelectedCard(null)
        }
        setMsg(t("CARD_DELETE_SUCCESS"))
        await fetchProfile();
      } else {
        setErr(err("CARD_DELETE_ERR"))
        setIsError(true)
      }
    } catch (err) {
      setErr(err("CARD_DELETE_ERR") + ": " + error?.response?.data?.error)
    }
  }

  const handleExchangeRate = async (amount, is_for_rate = false) => {
    try {
      const exchangeData = {
        type: activeTab.toUpperCase(),
        amount,
      };

      const response = await exchangeAPI.calculateExchange(exchangeData.type, exchangeData.amount);

      if (response.data) {
        console.log(response.data)
        const { rate_amount, uzs_amount } = response.data;

        setExchangeRate(rate_amount);
        console.log('exchange rate:', rate_amount);

        if (is_for_rate === false) {
          setToAmount(formatNumberWithCommas(uzs_amount));
        }
      } else {
        setToAmount(0);
      }
    } catch (err) {
      if (is_for_rate) {
        if (err?.response) {
          setErr(err.response?.data?.error);
        } else {
          setErr(t("NETWORK_ERR"))
        }
      }
      setToAmount(0);
    }
  };

  const handleDisable = () => {
    if (activeTab === "buy") {
      return !selectedCard || !selectedWallet || !selectedWallet?.verified || !fromAmount || !toAmount || fromAmount < 20
    } else {
      return !selectedCard || !fromAmount || !toAmount || !tronHash
    }
  }

  const handleCardSelect = (card) => setSelectedCard(card);
  const handleWalletSelect = (wallet) => setSelectedWallet(wallet);

  return (
    <div className="bg-white rounded-2xl p-5 mb-6 w-full shadow-md animate-fadeIn">
      {/* Поле "Отдаю" */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-600 mb-2">
          {activeTab === "buy" ? t("BUY_AMOUNT") : t("BUY_AMOUNT_TO_WRITTEN")}
        </label>
        <div className="relative">
          <input
            type="text"
            value={fromAmount}
            onChange={(e) => handleInputChange(e.target.value, setFromAmount, true)}
            className="w-full border-2 !text-black rounded-xl px-4 py-3 pr-28 text-lg font-semibold focus:border-blue-500 focus:ring-0"
            placeholder="0"
            inputMode="decimal"
          />
          <div
            className={`absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${getCurrencyColor(
              getFromCurrency()
            )}`}
          >
            {getCurrencyIcon(getFromCurrency())}
            {getFromCurrency()}
          </div>
        </div>


        {activeTab === "buy" && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              {getMethodLabel("USDT", false)}
            </label>
            <WalletDropdown
              onWalletDelete={handleWalletDelete}
              onWalletAdd={handleWalletAdd}
              selectedWallet={selectedWallet}
              onWalletSelect={handleWalletSelect}
              wallets={wallets}
            />
          </div>
        )}
      </div>

      {/* Поле "Получаю" */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-600 mb-2">
          {activeTab === "sell" ? t("SELL_AMOUNT") : t("SELL_AMOUNT_TO_WRITTEN")}
        </label>
        <div className="relative">
          <input
            type="text"
            value={toAmount}
            onChange={(e) => handleInputChange(e.target.value, setToAmount, false)}
            className="w-full !text-black border-2 rounded-xl px-4 py-3 pr-28 text-lg font-semibold focus:border-blue-500 focus:ring-0"
            placeholder="0"
            inputMode="decimal"
            disabled={true}
          />
          <div
            className={`absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${getCurrencyColor(
              getToCurrency()
            )}`}
          >
            {getCurrencyIcon(getToCurrency())}
            {getToCurrency()}
          </div>
        </div>

        {activeTab !== "buy" && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              {getMethodLabel("UZS", false)}
            </label>
            <CardDropdown
              selectedCard={selectedCard}
              onCardSelect={handleCardSelect}
              onCardAdd={handleCardAdd}
              onCardDelete={handleCardDelete}
              onCardOtp={handleCardOtp}
              cards={cards}
            />

          </div>
        )}

        {activeTab !== "buy" && (
          <div className="mt-3">
            <TronHashInput
              value={tronHash}
              onChange={handleHashChange}
              showAllert={showAlert}
            />
          </div>
        )}

        {activeTab === "buy" && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              {getMethodLabel("UZS", true)}
            </label>
            <CardDropdown
              selectedCard={selectedCard}
              onCardSelect={handleCardSelect}
              onCardAdd={handleCardAdd}
              onCardDelete={handleCardDelete}
              onCardOtp={handleCardOtp}
              cards={cards}
            />
          </div>
        )}
      </div>

      {/* Инфо о курсе */}
      <div className="mb-5 p-3 bg-blue-50 rounded-lg">
        <div className="text-sm text-gray-600">
          {t("RATE")}: 1 USDT = {formatNumberWithCommas(exchangeRate.toString())} UZS
        </div>
        {activeTab === "buy" && (
          <div>
            <div className="text-xs text-gray-500 mt-1">
              {t("YOU_BUY")}
            </div>
            <div className="mt-4 text-xs text-gray-600">
              {t("YOU_BUY_MSG")}
            </div>
          </div>
        )}
        {activeTab === "sell" && (
          <div>
            <div className="text-xs text-gray-500 mt-1">
              {t("YOU_SELL")}
            </div>
            <div className="mt-4 text-xs text-gray-600">
              {t("YOU_SELL_MSG")}
            </div>
          </div>
        )}

      </div>

      {/* Кнопка */}
      <button
        onClick={handleExchange}
        disabled={handleDisable() || requestLoading}
        className="w-full bg-gradient-to-r from-blue-500 to-blue-600 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-xl py-4 text-base font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition disabled:cursor-not-allowed disabled:transform-none"
      >
        {requestLoading ? (
          <div className="flex items-center justify-center gap-3">
            {activeTab === "buy" ? t("BUY_USDT") : t("SELL_USDT")}
            <div className="w-5 h-5 border-2 border-transparent border-t-white rounded-full animate-spin"></div>
          </div>
        ) : (
          activeTab === "buy" ? t("BUY_USDT") : t("SELL_USDT")
        )}
      </button>

      {/* Форма ввода OTP */}

      {showOtpForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-sm mx-4 p-6 bg-white rounded-2xl shadow-lg">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 text-center">
              {t("CONFIRMING_OPERATION")}
            </h2>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              {t("ENTER_SMS")}
            </label>
            <input
              type="text"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              className="w-full border-2 rounded-xl px-4 py-3 text-lg font-semibold focus:border-blue-500 focus:ring-0 text-sm !text-black"
              placeholder="******"
            />
            <button
              onClick={handleOtp}
              disabled={!otpCode || requestLoading}
              className="mt-4 w-full bg-green-500 text-white rounded-xl py-3 font-semibold shadow hover:bg-green-600 transition disabled:opacity-50"
            >
              {requestLoading ? (
                <div className="flex items-center justify-center gap-3">
                  {t("CONFIRM")}
                  <div className="w-5 h-5 border-2 border-transparent border-t-white rounded-full animate-spin"></div>
                </div>
              ) : (
                t("CONFIRM")
              )}
            </button>
            <button
              onClick={() => setShowOtpForm(false)}
              className="mt-2 w-full bg-gray-300 text-gray-800 rounded-xl py-2 font-medium hover:bg-gray-400 transition"
            >
              {t("CANCEL")}
            </button>
          </div>
        </div>
      )}
      {showMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-10">
          <div className="w-full max-w-sm mx-4 p-6 bg-white rounded-2xl shadow-lg">
            <h2 className={`text-lg font-semibold mb-4 text-center ${isError ? "text-red-600" : "text-green-600"
              }`}>
              {isError ? "Ошибка" : "Успех"}
            </h2>
            <div className="flex items-center justify-center h-12">
              <label className="text-sm font-medium text-gray-600">
                {showMessage}
              </label>
            </div>

            <button
              onClick={resetMessage}
              className="mt-4 w-full bg-green-500 text-white rounded-xl py-3 font-semibold shadow hover:bg-green-600 transition disabled:opacity-50"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangeForm;
